import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export enum UserRoles {
    Admin = 'admin',
    SuperAdmin = 'super-admin'
}

export interface UserElement {
    'id'?: number,
    'name': string,
    'okta_id': string,
    'role': UserRoles
}

@Injectable()
export class UserService {
    userList:BehaviorSubject<UserElement[] | null> = new BehaviorSubject<UserElement[] | null>(null);
    loadFailed = false;

    constructor( private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService ){};

    get usersUpdated(): Observable<UserElement[] | null> {
        return this.userList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        this.http.get<any>( environment.azure.url + '/admin.users', options)
            .subscribe({
                next: data => {
                    let loadedUsers: UserElement[] = [];
                    data.forEach((user:any) => {
                        loadedUsers.push({
                            'id': user.userID,
                            'name': user.name,
                            'okta_id': user.oktaID,
                            'role': user.role
                        });
                    });
                    this.userList.next ( loadedUsers );
                    this.loadFailed = false;
                },
                error: msg => { 
                    this.loadFailed = true;
                    this.userList.next(null);
                    console.log( msg );
                }
            });
    }

    add( target: UserElement ) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.users.add',
            {
                userID: target.id,
                name: target.name,
                oktaID: target.okta_id,
                role: target.role
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'User Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add user. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    remove( target: UserElement ) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.users.remove',
            {
                userID: target.id
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'User Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove user. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }

    update( target: UserElement ) {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = {headers: headers};

        let postSub = this.http.post<any>( environment.azure.url + '/admin.users.update',
            {
                userID: target.id,
                name: target.name,
                oktaID: target.okta_id,
                role: target.role
            },
            options
        )
        .subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'User Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update user. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log( msg );
                postSub.unsubscribe();
            }
        });
    }
}
